import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FiPlus, FiSearch, FiTrash2, FiX } from 'react-icons/fi';

const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-md text-blue-700 font-bold';

const GodConsumerModal = ({ mode = 'create', consumer = null, onSuccess, onCancel }) => {
    const [formData, setFormData] = useState({
        username: '',
        useremail: '',
        displayName: '',
        password: '',
        newPassword: '',
        confirmPassword: '',
        consumerType: 'consumer',
        oldSiteImport: false,
        resetPasswordForOldImport: false,
        favourite: []
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [favSearch, setFavSearch] = useState('');
    const [favResults, setFavResults] = useState([]);
    const [favModel, setFavModel] = useState('godMember');
    const [activeFavIndex, setActiveFavIndex] = useState(null);

    const authHeaders = () => ({
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'ngrok-skip-browser-warning': 'true'
    });

    useEffect(() => {
        if (mode === 'edit' && consumer) {
            setFormData({
                username: consumer.username || '',
                useremail: consumer.useremail || '',
                displayName: consumer.displayName || '',
                password: '',
                newPassword: '',
                confirmPassword: '',
                consumerType: consumer.consumerType || 'consumer',
                oldSiteImport: Boolean(consumer.oldSiteImport),
                resetPasswordForOldImport: Boolean(consumer.resetPasswordForOldImport),
                favourite: Array.isArray(consumer.favourite)
                    ? consumer.favourite.map((f) => ({
                        favouriteFor: f.favouriteFor?._id || f.favouriteFor,
                        favouriteForModel: f.favouriteForModel,
                        _label: f._label || ''
                    }))
                    : []
            });
        }
    }, [mode, consumer]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const formatGodMemberLabel = (m) => {
        if (!m) return '';
        return `${m.godID || ''} - ${m.godName || ''} - ${m.webID || ''}`.trim();
    };

    const formatNonMemberLabel = (m) => {
        if (!m) return '';
        return `${m.name || '—'} - ${m.webID || ''}`.trim();
    };

    const searchFavouriteTarget = async (term) => {
        if (!term.trim()) {
            setFavResults([]);
            return;
        }
        try {
            const base = process.env.REACT_APP_API_URL;
            const url = favModel === 'godMember'
                ? `${base}/api/godmembers?search=${encodeURIComponent(term)}&limit=30`
                : `${base}/api/nonmembers?search=${encodeURIComponent(term)}&limit=30`;
            const res = await axios.get(url, { headers: authHeaders() });
            if (res.data.success) setFavResults(res.data.data || []);
        } catch {
            setFavResults([]);
        }
    };

    const addFavourite = (item) => {
        const favouriteFor = item._id;
        const favouriteForModel = favModel;
        const exists = formData.favourite.some(
            (f) => String(f.favouriteFor) === String(favouriteFor) && f.favouriteForModel === favouriteForModel
        );
        if (exists) return;
        const _label = favModel === 'godMember' ? formatGodMemberLabel(item) : formatNonMemberLabel(item);
        setFormData((prev) => ({
            ...prev,
            favourite: [...prev.favourite, { favouriteFor, favouriteForModel, _label }]
        }));
        setFavSearch('');
        setFavResults([]);
        setActiveFavIndex(null);
    };

    const removeFavourite = (index) => {
        setFormData((prev) => ({
            ...prev,
            favourite: prev.favourite.filter((_, i) => i !== index)
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        if (mode === 'edit') {
            const hasNew = Boolean(formData.newPassword?.trim());
            const hasConfirm = Boolean(formData.confirmPassword?.trim());
            if (hasNew !== hasConfirm) {
                setError('Enter and confirm the new password, or leave both blank to keep the current password.');
                setLoading(false);
                return;
            }
            if (hasNew && formData.newPassword !== formData.confirmPassword) {
                setError('New password and confirmation do not match.');
                setLoading(false);
                return;
            }
        }

        try {
            const payload = {
                username: formData.username.trim() || undefined,
                useremail: formData.useremail.trim() || undefined,
                displayName: formData.displayName.trim() || undefined,
                consumerType: formData.consumerType,
                oldSiteImport: formData.oldSiteImport,
                resetPasswordForOldImport: formData.resetPasswordForOldImport,
                favourite: formData.favourite.map(({ favouriteFor, favouriteForModel }) => ({
                    favouriteFor,
                    favouriteForModel
                }))
            };

            if (mode === 'create') {
                if (formData.password?.trim()) payload.password = formData.password.trim();
            } else if (formData.newPassword?.trim()) {
                payload.password = formData.newPassword.trim();
            }

            const url = `${process.env.REACT_APP_API_URL}/api/consumers`;
            const response = mode === 'edit'
                ? await axios.put(`${url}/${consumer._id}`, payload, { headers: authHeaders() })
                : await axios.post(url, payload, { headers: authHeaders() });
            if (response.data.success) onSuccess(response.data.data);
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto p-4">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl my-8 max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold">{mode === 'edit' ? 'Edit Consumer' : 'Create Consumer'}</h2>
                    <button type="button" onClick={onCancel} className="text-gray-500 hover:text-gray-700" aria-label="Close">
                        <FiX className="w-6 h-6" />
                    </button>
                </div>
                {error && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md text-sm">{error}</div>}
                <form onSubmit={handleSubmit} className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                            <input name="username" value={formData.username} onChange={handleChange} className={inputClass} />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                            <input type="email" name="useremail" value={formData.useremail} onChange={handleChange} className={inputClass} />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Display name (optional)</label>
                            <input name="displayName" value={formData.displayName} onChange={handleChange} className={inputClass} placeholder="Shown name on site" />
                        </div>
                        {mode === 'create' ? (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                                <input
                                    type="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    className={inputClass}
                                    autoComplete="new-password"
                                />
                            </div>
                        ) : null}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                            <select name="consumerType" value={formData.consumerType} onChange={handleChange} className={inputClass}>
                                <option value="consumer">consumer</option>
                                <option value="mama">mama</option>
                            </select>
                        </div>
                    </div>

                    {mode === 'edit' && (
                        <div className="border border-amber-200 bg-amber-50/60 rounded-lg p-4 space-y-3">
                            <div>
                                <h3 className="text-sm font-semibold text-gray-800">Set new password</h3>
                                <p className="text-xs text-gray-600 mt-1">
                                    Leave both fields blank to keep the current login password. When you set a new password and save, the user can log in with it immediately.
                                </p>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">New password</label>
                                    <input
                                        type="password"
                                        name="newPassword"
                                        value={formData.newPassword}
                                        onChange={handleChange}
                                        className={inputClass}
                                        autoComplete="new-password"
                                        placeholder="Enter new password"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Confirm new password</label>
                                    <input
                                        type="password"
                                        name="confirmPassword"
                                        value={formData.confirmPassword}
                                        onChange={handleChange}
                                        className={inputClass}
                                        autoComplete="new-password"
                                        placeholder="Re-enter new password"
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="flex flex-wrap gap-4 text-sm">
                        <label className="flex items-center gap-2">
                            <input type="checkbox" name="oldSiteImport" checked={formData.oldSiteImport} onChange={handleChange} />
                            Old site import
                        </label>
                        <label className="flex items-center gap-2">
                            <input type="checkbox" name="resetPasswordForOldImport" checked={formData.resetPasswordForOldImport} onChange={handleChange} />
                            Reset password (old import)
                        </label>
                    </div>

                    <div className="border border-gray-200 rounded-lg p-3">
                        <div className="flex justify-between items-center mb-2">
                            <span className="font-medium text-gray-800">Favourites</span>
                        </div>
                        {formData.favourite.length > 0 && (
                            <ul className="mb-3 space-y-1 text-sm">
                                {formData.favourite.map((f, i) => (
                                    <li key={`${f.favouriteForModel}-${f.favouriteFor}-${i}`} className="flex justify-between items-center bg-gray-50 px-2 py-1 rounded">
                                        <span className="text-blue-700 truncate">
                                            [{f.favouriteForModel}] {f._label || f.favouriteFor}
                                        </span>
                                        <button type="button" onClick={() => removeFavourite(i)} className="text-red-600 p-1" aria-label="Remove favourite">
                                            <FiTrash2 />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                        <div className="flex gap-2 mb-2">
                            <select value={favModel} onChange={(e) => setFavModel(e.target.value)} className="border border-gray-300 rounded-md px-2 py-1 text-sm">
                                <option value="godMember">God Member</option>
                                <option value="NonMember">Non-Member</option>
                            </select>
                        </div>
                        <div className="relative">
                            <input
                                type="text"
                                value={favSearch}
                                onChange={(e) => {
                                    setFavSearch(e.target.value);
                                    setActiveFavIndex(0);
                                    searchFavouriteTarget(e.target.value);
                                }}
                                onFocus={() => setActiveFavIndex(0)}
                                placeholder={favModel === 'godMember' ? 'Search godID, name, webID...' : 'Search name, webID...'}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 pr-9 text-sm"
                            />
                            <FiSearch className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                            {activeFavIndex !== null && favResults.length > 0 && (
                                <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-40 overflow-auto">
                                    {favResults.map((item) => (
                                        <button
                                            key={item._id}
                                            type="button"
                                            className="w-full text-left p-2 hover:bg-gray-100 text-sm flex items-center gap-1"
                                            onClick={() => addFavourite(item)}
                                        >
                                            <FiPlus className="shrink-0" />
                                            {favModel === 'godMember' ? formatGodMemberLabel(item) : formatNonMemberLabel(item)}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t">
                        <button type="button" onClick={onCancel} className="px-4 py-2 bg-gray-100 rounded-md hover:bg-gray-200">Cancel</button>
                        <button type="submit" disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
                            {loading ? 'Saving...' : mode === 'edit' ? 'Update' : 'Create'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default GodConsumerModal;
